import type { CodeSessionEvent } from '@/lib/code-sessions/types';

import type { CodeSessionRow, CodeSessionSummary, CodeTurnDelivery } from './types';

import { deterministicPromptId, isTerminalTurnStatus } from '@/lib/code-sessions/turn-utils';

export type LocalTurnDispatch = {
  createdAt: number;
  queued: boolean;
  delivery?: CodeTurnDelivery;
};
export type TurnActivity = {
  id: string;
  status: 'starting' | 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'interrupted';
  startedAt?: number;
  endedAt?: number;
  /** Stop was requested for this live turn and it has not ended yet. */
  stopping?: true;
  /**
   * The first turn of the run this turn joined: the agent took it while that
   * run was still going, as it takes a steer, so its work continues the run.
   * Absent for a turn that starts a run of its own.
   */
  run?: string;
  /**
   * This tab sent it, and it went to the agent rather than into the tray: when it sent it, by
   * this tab's clock. Unlike `startedAt`, a saved turn does not replace it with the server's time.
   */
  sentAt?: number;
};

/**
 * The agent took this tab's send rather than the tray: sent to an idle agent, or a steer, which
 * cuts into its next step, whether sent as one or sent now from the tray.
 */
function reachedAgent(fold: ActivityFold, dispatch: LocalTurnDispatch): boolean {
  return !dispatch.queued || fold.delivery === 'steer';
}

/** Running, or about to run: either way it counts as work, clock and header included. */
export function isWorkingTurn(turn: Pick<TurnActivity, 'status'>): boolean {
  return turn.status === 'running' || turn.status === 'starting';
}

/**
 * Whether a session is working, as its composer and its sidebar row both show
 * it: a turn is starting, running or waiting, or the session row is running
 * while a turn waits. Between queued turns the turn runner ends one and claims
 * the next while the session stays running, since a turn waiting in cubic is
 * live. An idle session's queued turns are not work.
 */
export function isSessionWorking(
  sessionStatus: CodeSessionRow['status'] | undefined,
  activity: readonly Pick<TurnActivity, 'status' | 'endedAt'>[],
  /** A newer list row's verdict (`listedWork`): the session works, or no queue runs any more. */
  listed?: ListedWork,
): boolean {
  if (listedStartsWork(listed, activity)) {
    return true;
  }
  // Only a newer idle row says no queue runs; a working row that a later end overrode says nothing
  // of the queue, which the cached status still describes.
  const runsQueue = sessionStatus === 'running' && listed?.working !== false;
  return activity.some(
    (turn) =>
      isWorkingTurn(turn) || turn.status === 'waiting' || (runsQueue && turn.status === 'queued'),
  );
}

/**
 * Whether a newer list row says the session works and the page saw no turn end after the row's
 * time: a later end on the page is newer news than the row, and the next list read agrees with it.
 */
export function listedStartsWork(
  listed: ListedWork | undefined,
  activity: readonly Pick<TurnActivity, 'endedAt'>[],
): boolean {
  return (
    listed?.working === true &&
    !activity.some((turn) => turn.endedAt !== undefined && turn.endedAt > listed.at)
  );
}

/**
 * A session's list row's verdict on its work, where the row is newer than the cached detail:
 * whether the session works, a question waiting on the person included, as of the row's last
 * activity, which a turn's end moves (`turn-end.ts`).
 */
export type ListedWork = { working: boolean; at: number };

/**
 * The list row's verdict (`ListedWork`) where the row reads later than the cached detail.
 * Undefined when either copy is missing or the row reads no later, so the detail decides.
 */
export function listedWork(
  listed: Pick<CodeSessionSummary, 'status' | 'lastActivityAt'> | undefined,
  cached: Pick<CodeSessionRow, 'lastActivityAt'> | undefined,
): ListedWork | undefined {
  if (listed === undefined || cached === undefined) {
    return undefined;
  }
  const at = Date.parse(listed.lastActivityAt);
  return at > Date.parse(cached.lastActivityAt)
    ? { working: listed.status === 'creating' || listed.status === 'running', at }
    : undefined;
}

/** The turn fields that activity reads, with times in epoch milliseconds. */
type ActivityTurn = {
  id: string;
  index: number;
  status: string;
  delivery: CodeTurnDelivery;
  inboxId: string | null;
  createdAt: number;
  startedAt?: number;
  endedAt?: number | null;
};

/** A turn's status and end as the fold settles them; its start is resolved once, at the end. */
type ActivityFold = {
  id: string;
  /** Place among session turns, from a saved row or turn event; absent for a local-only send. */
  index?: number;
  status: TurnActivity['status'];
  endedAt?: number;
  /**
   * When it was taken: a saved turn's start, which is when cubic admitted it,
   * else when the agent was seen taking it, by its running event or its delivery.
   */
  takenAt?: number;
  /** When it existed by: a saved turn's creation, else its first event; absent for a local-only send. */
  existedBy?: number;
  /** How a saved turn or local dispatch was sent; absent for event-only turns. */
  delivery?: ActivityTurn['delivery'];
  /** It counted as work before it ended, so this tab's send time can start its clock. */
  ran: boolean;
};

/** Whether a turn has ended: done, failed, or interrupted. */
export function isTerminalActivity(turn: TurnActivity): boolean {
  return isTerminalTurnStatus(turn.status);
}

/**
 * Work runs from a prompt's send to its end when it went to an idle session.
 * The session's first turn waits on the sandbox boot, which has its own rows,
 * and a follow-up sent while an earlier turn was still going waits in the
 * queue, so both count from when cubic took them. A turn cubic never took has
 * no start. Only saved times are used, so a reload shows the same duration.
 */
function savedWorkStart(
  turn: ActivityTurn,
  waited: ReadonlySet<string>,
  firstIndex: number,
): number | undefined {
  if (turn.startedAt === undefined) {
    return undefined;
  }
  return turn.index === firstIndex || waited.has(turn.id) ? turn.startedAt : turn.createdAt;
}

/**
 * The follow-ups sent while an earlier turn had not ended, so they waited behind it. A steer is sent
 * while a turn is going and goes straight to the agent, so it never waited.
 */
function turnsThatWaited(turns: readonly ActivityTurn[]): Set<string> {
  const waited = new Set<string>();
  let lastEnd = -Infinity;
  for (const turn of [...turns].sort((a, b) => a.index - b.index)) {
    if (turn.delivery === 'queue' && lastEnd > turn.createdAt) {
      waited.add(turn.id);
    }
    lastEnd = Math.max(lastEnd, turn.endedAt ?? Infinity);
  }
  return waited;
}

/**
 * Only turn, inbox, and session status events move a turn; token streams do
 * not. The session controller keeps these in their own list so activity reads
 * skip the rest of the log.
 */
export function movesTurnActivity(event: CodeSessionEvent): boolean {
  return (
    event.kind === 'turn' ||
    event.kind === 'session_status' ||
    (event.kind === 'agent' && event.type.startsWith('session.inbox.'))
  );
}

/**
 * How a turn that waits for the agent as `status` reads once cubic admitted it.
 * cubic admits a follow-up only once nothing would reach the agent before it:
 * the turn runner claimed it as the head of the queue, or it went to an idle
 * session in its own request. So it is starting, and leaves the tray as its
 * send begins. An admitted steer still waits for the agent's next step. An event-only
 * admission with unknown delivery conservatively leaves the queue too.
 */
function admittedStatus(
  status: TurnActivity['status'],
  delivery: CodeTurnDelivery | undefined,
): TurnActivity['status'] {
  return status === 'queued' && delivery !== 'steer' ? 'starting' : status;
}

/**
 * How a saved turn reads before events move it, given how this tab's own send
 * of it reads (`local`), if it sent it.
 */
function savedTurnStatus(
  turn: ActivityTurn,
  local: TurnActivity['status'] | undefined,
): TurnActivity['status'] {
  if (isTerminalTurnStatus(turn.status) || turn.status === 'running') {
    return turn.status;
  }
  const waiting = local ?? (turn.index === 0 ? 'starting' : 'queued');
  return turn.status === 'admitted' ? admittedStatus(waiting, turn.delivery) : waiting;
}

/**
 * Ends the turns a newer list row says had ended (`listedWork`). A session goes idle only
 * once no turn is live, so the turns that existed by the list's time ended. Saving a prompt moves
 * the session's last activity before cubic admits it and saves its start (`promptCodeSession`), so
 * a turn the agent took at or after that time keeps what its row and events say, and so does a
 * prompt sent here that the agent has not taken. The list does not say how or when a turn ended,
 * so it reads as done, for no duration, until its own end arrives.
 */
function endListedWork(
  folds: Iterable<ActivityFold>,
  listed: ListedWork | undefined,
  dispatches: Readonly<Record<string, LocalTurnDispatch>>,
) {
  if (listed === undefined || listed.working) {
    return;
  }
  const endedAt = listed.at;
  for (const fold of folds) {
    const ended =
      fold.takenAt === undefined
        ? fold.existedBy !== undefined &&
          fold.existedBy < endedAt &&
          !Object.hasOwn(dispatches, fold.id)
        : fold.takenAt < endedAt;
    if (ended && !isTerminalTurnStatus(fold.status)) {
      fold.status = 'done';
    }
  }
}

/** Like T3's local dispatch state, an idle send counts as work before server acknowledgment. */
export function deriveTurnActivity(
  turns: readonly ActivityTurn[],
  events: readonly CodeSessionEvent[],
  dispatches: Readonly<Record<string, LocalTurnDispatch>>,
  options: {
    awaitingInput?: boolean;
    /** A newer list row's verdict (`listedWork`): the turns end where it says the work had. */
    listed?: ListedWork;
  } = {},
): TurnActivity[] {
  const byId = new Map<string, ActivityFold>();
  for (const [id, dispatch] of Object.entries(dispatches)) {
    byId.set(id, {
      id,
      status: dispatch.queued ? 'queued' : 'running',
      delivery: dispatch.delivery,
      ran: !dispatch.queued,
    });
  }
  const savedById = new Map(turns.map((turn) => [turn.id, turn]));
  const firstIndex = Math.min(...turns.map((turn) => turn.index));
  const waited = turnsThatWaited(turns);
  const savedStart = (id: string) => {
    const turn = savedById.get(id);
    return turn === undefined ? undefined : savedWorkStart(turn, waited, firstIndex);
  };
  // This tab's own send time, for a send to an idle session; a queued send has none to count from.
  const idleSendAt = (fold: ActivityFold) => {
    const dispatch = dispatches[fold.id];
    return dispatch !== undefined && !dispatch.queued ? dispatch.createdAt : undefined;
  };
  // An idle send's running clock keeps this tab's own send time, so a snapshot cannot move it;
  // a queued send's clock starts when cubic takes it.
  const liveStart = (fold: ActivityFold) => idleSendAt(fold) ?? savedStart(fold.id) ?? fold.takenAt;
  // Settle on the saved start a reload will show once the row catches up.
  const settledStart = (fold: ActivityFold) =>
    savedStart(fold.id) ?? (fold.ran ? idleSendAt(fold) : undefined) ?? fold.takenAt;
  const take = (fold: ActivityFold, at: number) => {
    fold.status = 'running';
    fold.ran = true;
    fold.takenAt ??= at;
  };
  for (const turn of turns) {
    const status = savedTurnStatus(turn, byId.get(turn.id)?.status);
    byId.set(turn.id, {
      id: turn.id,
      index: turn.index,
      status,
      endedAt: turn.endedAt ?? undefined,
      takenAt: turn.startedAt,
      existedBy: turn.createdAt,
      delivery: turn.delivery,
      ran: status === 'running',
    });
  }
  const turnIdByInbox = new Map([...byId.keys()].map((id) => [deterministicPromptId(id), id]));
  for (const turn of turns) {
    if (turn.inboxId !== null) {
      turnIdByInbox.set(turn.inboxId, turn.id);
    }
  }
  for (const event of [...events].sort((a, b) => a.seq - b.seq)) {
    if (event.kind === 'turn') {
      const fold: ActivityFold = byId.get(event.turnId) ?? {
        id: event.turnId,
        status: event.index === 0 ? 'starting' : 'queued',
        existedBy: event.at,
        ran: false,
      };
      fold.index ??= event.index;
      // Cached pre-terminal events must not revive a completed snapshot.
      if (isTerminalTurnStatus(fold.status)) {
        if (event.status === fold.status) {
          fold.endedAt ??= event.at;
        }
        if (event.status === 'running') {
          fold.takenAt ??= event.at;
        }
      } else if (event.status === 'running') {
        take(fold, event.at);
      } else if (isTerminalTurnStatus(event.status)) {
        fold.status = event.status;
        fold.endedAt = event.at;
      } else if (event.status === 'admitted') {
        fold.status = admittedStatus(fold.status, fold.delivery);
      }
      byId.set(event.turnId, fold);
      turnIdByInbox.set(deterministicPromptId(event.turnId), event.turnId);
    } else if (
      event.kind === 'agent' &&
      (event.type === 'session.inbox.delivered' || event.type === 'session.inbox.cancelled')
    ) {
      const data = event.data as { inboxID?: string; id?: string } | null;
      const id = turnIdByInbox.get(data?.inboxID ?? data?.id ?? '');
      const fold = id === undefined ? undefined : byId.get(id);
      // OpenCode delivery can arrive before Cubic's running event or DB update.
      // Use the same evidence for the working state and removal from the tray.
      if (fold && !isTerminalTurnStatus(fold.status)) {
        if (event.type === 'session.inbox.delivered') {
          take(fold, event.at);
        } else {
          fold.status = 'interrupted';
          fold.endedAt = event.at;
        }
      }
    } else if (
      event.kind === 'session_status' &&
      (event.status === 'failed' || event.status === 'stopped' || event.status === 'archived')
    ) {
      for (const fold of byId.values()) {
        const since =
          liveStart(fold) ?? savedById.get(fold.id)?.createdAt ?? dispatches[fold.id]?.createdAt;
        if (!isTerminalTurnStatus(fold.status) && since !== undefined && event.at >= since) {
          fold.status = event.status === 'failed' ? 'failed' : 'interrupted';
          fold.endedAt = event.at;
        }
      }
    }
  }
  endListedWork(byId.values(), options.listed, dispatches);
  const startOf = (fold: ActivityFold): number | undefined => {
    switch (fold.status) {
      case 'queued':
        return undefined;
      case 'starting':
      case 'running':
      case 'waiting':
        return liveStart(fold);
      case 'done':
      case 'failed':
      case 'interrupted':
        return settledStart(fold);
      default: {
        const unhandled: never = fold.status;
        return unhandled;
      }
    }
  };
  const runOf = runFirstTurns(byId.values());
  return [...byId.values()].map((fold) => {
    const run = runOf.get(fold.id);
    const dispatch: LocalTurnDispatch | undefined = dispatches[fold.id];
    return {
      id: fold.id,
      status: options.awaitingInput && fold.status === 'running' ? 'waiting' : fold.status,
      startedAt: startOf(fold),
      endedAt: fold.endedAt,
      ...(run === undefined ? {} : { run }),
      ...(dispatch !== undefined && reachedAgent(fold, dispatch)
        ? { sentAt: dispatch.createdAt }
        : {}),
    };
  });
}

/** A run as it reads so far: live while any of its turns is, else ended at its last end. */
type RunSoFar = { id: string; live: boolean; endedAt: number | undefined };

/**
 * The first turn of the run each turn joined, for the turns that joined one.
 * OpenCode delivers a steer into the execution already running, so a steer
 * belongs to the first run that had not ended when it was taken. It takes a
 * queued prompt only once the run ahead of it ended, which is also when cubic
 * admits the prompt and saves its start: a queued prompt starts a run of its
 * own, and a steer sent after it can still belong to the run before.
 */
function runFirstTurns(folds: Iterable<ActivityFold>): Map<string, string> {
  const runOf = new Map<string, string>();
  const runs: RunSoFar[] = [];
  // A turn not saved yet comes after the saved ones, and a send only this tab knows of after all.
  const place = (fold: ActivityFold) => fold.index ?? Number.MAX_SAFE_INTEGER;
  for (const fold of [...folds].sort((a, b) => place(a) - place(b))) {
    const joined =
      fold.delivery === 'queue' ? undefined : runs.find((run) => takenDuring(run, fold.takenAt));
    const run = joined ?? { id: fold.id, live: false, endedAt: undefined };
    if (joined === undefined) {
      runs.push(run);
    } else {
      runOf.set(fold.id, run.id);
    }
    if (isTerminalTurnStatus(fold.status)) {
      if (fold.endedAt !== undefined) {
        run.endedAt = Math.max(run.endedAt ?? fold.endedAt, fold.endedAt);
      }
    } else if (fold.status !== 'queued') {
      run.live = true;
    }
  }
  return runOf;
}

function takenDuring(run: RunSoFar, takenAt: number | undefined): boolean {
  return (
    takenAt !== undefined && (run.live || (run.endedAt !== undefined && takenAt < run.endedAt))
  );
}

/**
 * A run reads as the first of these any of its turns has. One still working
 * keeps the run working while a message it took waits for the agent's next
 * step; once all ended, one failed or stopped turn fails or stops the run.
 */
const RUN_STATUS_ORDER = [
  'running',
  'starting',
  'waiting',
  'queued',
  'failed',
  'interrupted',
  'done',
] as const satisfies readonly TurnActivity['status'][];

/**
 * Each run's activity, by its first turn's ID: working while any of its turns
 * is, timed from the first turn's start to the last turn's end, so the header
 * counts the work since the prompt that started it.
 */
export function runActivityById(activity: readonly TurnActivity[]): Map<string, TurnActivity> {
  const turnsByRun = new Map<string, TurnActivity[]>();
  for (const turn of activity) {
    const id = turn.run ?? turn.id;
    const turns = turnsByRun.get(id) ?? [];
    turns.push(turn);
    turnsByRun.set(id, turns);
  }
  return new Map([...turnsByRun].map(([id, turns]) => [id, runActivity(id, turns)]));
}

function runActivity(id: string, turns: readonly TurnActivity[]): TurnActivity {
  const first = turns.find((turn) => turn.id === id) ?? turns[0]!;
  if (turns.length === 1) {
    return first;
  }
  const status =
    RUN_STATUS_ORDER.find((order) => turns.some((turn) => turn.status === order)) ?? first.status;
  const ends = turns.flatMap((turn) => (turn.endedAt === undefined ? [] : [turn.endedAt]));
  return {
    id,
    status,
    ...(first.startedAt === undefined ? {} : { startedAt: first.startedAt }),
    ...(isTerminalTurnStatus(status) && ends.length > 0 ? { endedAt: Math.max(...ends) } : {}),
    ...(turns.some((turn) => turn.stopping) ? { stopping: true as const } : {}),
  };
}
